import { Button, InlineAlert } from '@design-systems/canvas-ui'
import React, { ReactNode, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { messages } from './messages'
import Icon, { RefreshIcon } from '@design-systems/react-icons'
import { useLogClick, usePageView } from 'utils/analytics'

interface BaseProps {
    count?: number
    children?: ReactNode
    className?: string
    onRetry?: () => void
    pageViewName?: string
}

interface DefaultErrorProps extends BaseProps {
    variant?: undefined | 'error'
    error: unknown
}

interface CustomProps extends BaseProps {
    variant: 'custom'
    error?: unknown
}

interface NoDataProps extends BaseProps {
    variant: 'no-data'
    error?: unknown
}

export type ErrorAlertProps = DefaultErrorProps | CustomProps | NoDataProps

const isExpiredSessionError = (err?: Error | null) =>
    !!err?.message?.includes('401')

export const isTimeoutError = (err?: Error | null) =>
    !!err?.message?.includes('timed out')

// Converts any unknown error (string, object, etc.) into a standard Error instance.
const normalizeError = (err: unknown): Error => {
    if (err instanceof Error) return err
    if (typeof err === 'string') return new Error(err)
    try {
        return new Error(JSON.stringify(err))
    } catch {
        return new Error('Unknown error')
    }
}

export const ErrorAlert: React.FC<ErrorAlertProps> = ({
    error,
    count,
    children,
    className,
    variant = 'error',
    onRetry,
    pageViewName,
}) => {
    const { t } = useTranslation()
    const { logClick } = useLogClick('signal timeout error page')
    const { trackPageView } = usePageView(pageViewName || 'error-alert')

    // Only normalize the error when needed.
    const normalizedError = useMemo(() => {
        if (!error) return undefined
        return normalizeError(error)
    }, [error])

    useEffect(() => {
        if (!pageViewName) {
            return
        }

        trackPageView()
    }, [pageViewName, trackPageView])

    if (normalizedError && isExpiredSessionError(normalizedError)) {
        return (
            <InlineAlert
                variant="error"
                heading={normalizedError.message}
                className={className}
            >
                <span>{messages.expiredSession(count)}</span>
                {children}
            </InlineAlert>
        )
    }

    if (normalizedError && isTimeoutError(normalizedError)) {
        return (
            <InlineAlert
                variant="error"
                heading={t('alert.generic')}
                className={className}
            >
                <div>
                    {onRetry && (
                        <Button
                            onClick={() => {
                                logClick('retry')
                                onRetry()
                            }}
                            className="hc8-mt-3x"
                            aria-label={t('alert.retry')}
                        >
                            <Icon svg={RefreshIcon} aria-hidden />
                            <span>{t('alert.retry')}</span>
                        </Button>
                    )}
                    {children}
                </div>
            </InlineAlert>
        )
    }

    if (variant === 'no-data') {
        return (
            <InlineAlert
                variant="info"
                heading={t('alert.noData.heading')}
                className={className}
            >
                {children}
            </InlineAlert>
        )
    }

    if (variant === 'custom') {
        return (
            <InlineAlert
                variant="error"
                heading={normalizedError?.message || t('alert.heading')}
                className={className}
            >
                {children}
            </InlineAlert>
        )
    }

    return (
        <InlineAlert
            variant="error"
            heading={t('alert.heading')}
            className={className}
        >
            <span>{t('alert.generic')}</span>
            {children}
        </InlineAlert>
    )
}
